import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Database, Target, Zap, TrendingUp, Search, Shield } from 'lucide-react';

const capabilities = [
  {
    icon: Database,
    title: 'Unified Data Layer',
    description: 'Connect Amazon Ads, Seller Central, Vendor Central, AMC, and DSP in one place.',
  },
  {
    icon: Zap,
    title: 'AI Processing Engine',
    description: '100+ tools analyze your data to surface insights and opportunities.',
  },
  {
    icon: TrendingUp,
    title: 'Performance Analytics',
    description: 'Real-time dashboards with the metrics that matter to your business.',
  },
  {
    icon: Search,
    title: 'Search Intelligence',
    description: 'Keyword research, ranking tracking, and competitive analysis.',
  },
  {
    icon: Target,
    title: 'Campaign Management',
    description: 'Full-service PPC management with AI-powered optimization.',
  },
  {
    icon: Shield,
    title: 'Buy Box Monitoring',
    description: 'Track pricing, availability, and competitor activity 24/7.',
  },
];

export default function PlatformSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });

  return (
    <section
      id="platform"
      ref={sectionRef}
      className="relative bg-[#05060B] grid-bg py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.08)_0%,transparent_70%)] pointer-events-none" />

      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div 
            className={`text-center mb-16 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
              Our Platform
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              One platform.{' '}
              <span className="text-cyan-400 cyan-glow">Every insight.</span>
            </h2>
            <p className="text-lg text-dark-400 max-w-2xl mx-auto">
              Our proprietary platform connects all your Amazon data sources and applies AI to surface actionable intelligence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className={`glass-card p-6 group hover-lift ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                  <capability.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2 group-hover:text-cyan-400 transition-colors">
                  {capability.title}
                </h3>
                <p className="text-dark-400 text-sm leading-relaxed">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>

          <div 
            className={`mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {[
              { value: '100+', label: 'Data Tools' },
              { value: '24/7', label: 'Monitoring' },
              { value: '<2min', label: 'Alert Speed' },
              { value: '99.9%', label: 'Uptime' },
            ].map((stat, index) => (
              <div 
                key={index} 
                className="text-center p-4 rounded-xl bg-dark-900/30 border border-cyan-500/10"
              >
                <div className="text-2xl md:text-3xl font-bold text-cyan-400 mb-1">{stat.value}</div>
                <div className="text-sm text-dark-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
